import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type { UsageStatus } from "@/utils/subscription.util";

import type { IPlanIdentity, IPlanResource } from "../types";
import { PlanActions } from "./PlanActions";
import { PlanResourceIcon } from "./PlanResourceIcon";
import { PlanUsageRing } from "./PlanUsageRing";
import { PlanUsageRow } from "./PlanUsageRow";

interface PlanPopoverContentProps {
  plan: IPlanIdentity;
  resources: IPlanResource[];
  /** Hero ring — mirrors the header pill (worst resource wins). */
  ringPercentage: number;
  ringStatus: UsageStatus;
  onUpgradeSeats?: () => void;
  onBuyTokens: () => void;
  onManageBilling: () => void;
}

/**
 * The plan dropdown: a hero band that restates the header ring in context,
 * one row per metered resource, the upgrade actions, then a billing link.
 *
 * Sections own their own padding, so the popover renders this with `p-0`.
 */
export const PlanPopoverContent = ({
  plan,
  resources,
  ringPercentage,
  ringStatus,
  onUpgradeSeats,
  onBuyTokens,
  onManageBilling,
}: PlanPopoverContentProps) => {
  const { t } = useTranslation(["common", "company", "nav"]);

  return (
    <div className="flex flex-col">
      {/* ── Hero ── */}
      <div className="relative overflow-hidden border-b border-border/40 bg-linear-to-br from-primary/10 via-primary/5 to-transparent px-4 py-3.5">
        <div className="pointer-events-none absolute -top-8 -right-8 size-24 rounded-full bg-primary/10 blur-2xl" />

        <div className="relative flex items-center gap-3">
          <PlanUsageRing
            percentage={ringPercentage}
            status={ringStatus}
            size={42}
            strokeWidth={3.5}
            trackClassName="stroke-surface"
          >
            <PlanResourceIcon resource="tokens" size={22} />
          </PlanUsageRing>

          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold tracking-widest text-muted-foreground">
              {t("common:chrome.your_plan")}
            </p>
            <div className="flex items-center gap-1.5">
              <p className="truncate text-[15px] font-bold text-foreground">
                {plan.name ?? t("nav:modules.subscription")}
              </p>
              {plan.isTrialing && (
                <Badge
                  variant="warning"
                  className="shrink-0 rounded-full px-1.5 py-0 text-[9px] font-bold"
                >
                  {t("common:chrome.trial")}
                </Badge>
              )}
            </div>
            {plan.renewalNote && (
              <p className="truncate text-[11px] font-medium text-muted-foreground">
                {plan.renewalNote}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Metered resources ── */}
      <div className="divide-y divide-border/40">
        {resources.map((resource, index) => (
          <PlanUsageRow
            key={resource.key}
            resource={resource}
            delay={index * 70}
          />
        ))}
      </div>

      {/* ── Actions ── */}
      <div className="border-t border-border/40 px-4 py-3">
        <PlanActions
          onUpgradeSeats={onUpgradeSeats}
          onBuyTokens={onBuyTokens}
        />
      </div>

      {/* ── Billing ── */}
      <button
        type="button"
        onClick={onManageBilling}
        className="group flex items-center justify-between border-t border-border/40 px-4 py-2.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/5"
      >
        {t("company:subscription.manage_plan_and_billing")}
        <ArrowRight
          size={13}
          className="transition-transform group-hover:translate-x-0.5 rtl-flip"
        />
      </button>
    </div>
  );
};
